import React, { Fragment, useState, useEffect, useRef } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { shape, string, oneOf, func, bool, node, object } from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Alert,
} from 'reactstrap';
import cn from 'classnames';
import CustomControl from '../CustomControl/CustomControl';
import Card from '../Card/Card';
import { randomString, strignShuffle } from '../../utils/utils';
import Hint from '../Hint/Hint';
import Icon from '../Icon/Icon';
// import { Link } from 'react-router-dom';

const propTypes = {
  direct_link_without_redirect: shape({
    scheme: oneOf(['http', 'https']),
    domain: string,
  }).isRequired,
  onChange: func.isRequired,
  value: string.isRequired,
  checkDomain: func.isRequired,
  enable: bool.isRequired,
  usageToggle: func.isRequired,
  innerRef: object.isRequired,
  error: string,
  intl: intlShape.isRequired,
};

const FlowDirectLinkForm = ({
  direct_link_without_redirect = {},
  onChange,
  value,
  enable,
  usageToggle,
  innerRef,
  error,
  checkDomain,
  intl,
  domainParking,
  domainParkingToggle,
  onDomianParkingInputChange,
  offerCategories,
}) => {
  const [isError, setErrorState] = useState(false);
  const [isAlertOpen, setAlertOpenState] = useState(true);
  const [isSaved, setSavedState] = useState(false);

  const theme = useSelector(state => state.webmaster.profile.theme);

  const domain = `${direct_link_without_redirect.scheme}://${direct_link_without_redirect.domain}`;
  const domainExample = `${direct_link_without_redirect.scheme}://example.${direct_link_without_redirect.domain}`;

  const regex = new RegExp(`^[a-zA-Z0-9-]*$`);

  const inputRef = useRef(null);

  const dismissAlert = () => {
    setAlertOpenState(false);
  };

  const handleChange = ({ target }) => {
    const { value } = target;
    if (regex.test(value) || value === '') {
      onChange(value);
      if (isError) {
        setErrorState(false);
      }
      if (isSaved) {
        setSavedState(false);
      }
    } else {
      if (!isError) {
        setErrorState(true);
      }
    }
  };

  const handleFocus = () => {
    if (regex.test(value) || value === '') {
      setErrorState(false);
    }
  };

  const handleBlur = () => {
    if (regex.test(value) || value === '') {
      setErrorState(false);
    }

    //  catch, чтобы не возникало unhandled promise rejection
    // подумать как избежать костылизации
    checkDomain().catch(() => {
      return null;
    });
  };

  const handleSave = () => {
    if (enable && value) {
      setSavedState(true);
    }
  };

  const getPlaceholder = () =>
    intl.formatMessage({
      id: 'flow.direct-link-landing.placeholder',
      defaultMessage: 'Enter The Title',
    });

  useEffect(() => {
    // если включена прямая ссылка и в ней есть значение
    // то меняем кнопку на "Сохранено"
    handleSave();
  }, []);

  return (
    <div ref={innerRef}>
      <Card id="direct-link-card">
        <Card.Header>
          <FormattedMessage
            id="flow.form.direct-link-without-redirect.title"
            defaultMessage="Direct link without redirect"
          />
          <Hint
            id="direct-link-form-tooltip"
            text={
              <FormattedMessage
                id="flow.form.direct-link-without-redirect.hint"
                defaultMessage="When using this tool, the operation of the prelanders and the A / B test function is limited"
              />
            }
          />
          {enable || domainParking.enabled ? (
            <div className="flow-direct-link-alert-wrapper">
              <Alert
                className="flow-direct-link-control-text"
                color="warning"
                isOpen={isAlertOpen}
                toggle={dismissAlert}
              >
                <Icon type="important" />
                <FormattedMessage
                  id="flow.form.direct-link-without-redirect.warning"
                  defaultMessage="The direct link will be active for 5 minutes after creation"
                />
              </Alert>
            </div>
          ) : null}
        </Card.Header>
        <Card.Body>
          <div className="flow-direct-link-grid">
            <div>
              <div className="flow-direct-link-control">
                <FormGroup>
                  <CustomControl
                    id="our-domain-usage"
                    value={'our-domain-usage'}
                    checked={enable}
                    onChange={usageToggle}
                    type="checkbox"
                    className="custom-control-xs"
                    label={
                      <FormattedMessage
                        id="flow.form.direct-link-without-redirect.use-our-domain"
                        defaultMessage="Use our domain"
                      />
                    }
                  />
                </FormGroup>
              </div>
              <Row className="flow-direct-link-row">
                <Col lg={12}>
                  <FormGroup className={cn({ 'has-error': isError || error })}>
                    <FormattedMessage
                      id="flow.form.direct-link-without-redirect.domain"
                      defaultMessage="Domain"
                    >
                      {string => (
                        <Fragment>
                          <Label htmlFor="subdomain">{string}</Label>
                          {error && <span className="inline-error">{error}</span>}
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>
                                {direct_link_without_redirect.scheme}://
                              </InputGroupText>
                            </InputGroupAddon>
                            <Input
                              innerRef={inputRef}
                              id="subdomain"
                              placeholder={getPlaceholder()}
                              disabled={!enable}
                              name="direct_link_without_redirect"
                              onChange={handleChange}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                              value={value}
                            />
                            <InputGroupAddon addonType="append">
                              <InputGroupText>
                                .{direct_link_without_redirect.domain}
                              </InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                        </Fragment>
                      )}
                    </FormattedMessage>
                  </FormGroup>

                  <small className={cn('form-text', { 'inline-error': isError })}>
                    <FormattedMessage
                      id="flow.form.direct-link-without-redirect.error"
                      defaultMessage="In a subdomain name field you are allowed to use numbers and latin letters only"
                    />
                  </small>

                  <i className="form-text">
                    <FormattedMessage
                      id="flow.direct-link-landing.example"
                      defaultMessage="Example: {domainExample}"
                      values={{ domainExample }}
                    />
                  </i>
                </Col>
                {/* <Col lg={buttonColumns}>
                  <FormGroup className="flow-direct-link-save">
                    <Button
                      color="primary"
                      type="button"
                      onClick={handleSave}
                      disabled={!enable || !value || isSaved}
                    >
                      {isSaved ? (
                        <FormattedMessage id="main.saved" defaultMessage="Saved" />
                      ) : (
                        <FormattedMessage id="main.save" defaultMessage="Save" />
                      )}
                    </Button>
                  </FormGroup>
                </Col> */}
              </Row>
            </div>

            {/* DOMAIN */}
            {offerCategories && offerCategories?.includes(40) ? null : (
              <div>
                <CustomControl
                  id="own-domain-usage"
                  value={'own-domain-usage'}
                  checked={domainParking.enabled}
                  onChange={domainParkingToggle}
                  type="checkbox"
                  className="custom-control-xs flow-own-domain-control"
                  label={
                    <FormattedMessage
                      id="flow.form.park-domain.control"
                      defaultMessage="Park your own domain"
                    />
                  }
                />
                <FormGroup>
                  <Label>
                    <FormattedMessage
                      id="flow.form.direct-link-without-redirect.domain"
                      defaultMessage="Domain"
                    />
                  </Label>
                  <Input
                    // innerRef={inputRef}
                    id="owndomain"
                    placeholder="subdomain.example.com"
                    disabled={!domainParking.enabled}
                    name="own_domain_name"
                    onChange={e => onDomianParkingInputChange(e.target.value)}
                    // onFocus={handleFocus}
                    // onBlur={handleBlur}
                    value={domainParking.domainName}
                  />
                </FormGroup>
                <p style={{ fontSize: 14 }}>
                  <FormattedMessage
                    id="flow.form.park-domian.guide"
                    defaultMessage="To park the domain, type in following NS records: {ns1} and {ns2}"
                    values={{
                      ns1: <span className="text-danger">ns1.mrrffo.com</span>,
                      ns2: <span className="text-danger">ns2.mrrffo.com</span>,
                    }}
                  />
                </p>
                <p style={{ fontSize: 14 }}>
                  <em>
                    <strong>
                      <FormattedMessage
                        id="flow.form.park-domian.warning.title"
                        defaultMessage="Attention!"
                      />
                    </strong>{' '}
                    <FormattedMessage
                      id="flow.form.park-domian.warning.text"
                      defaultMessage="NS entries need to be specified for primary domain example.com only, no need to specify anything for subdomains"
                    />
                  </em>
                </p>
                <a href="https://my.offerrum.com/faq/tools/parking/types/landing-type" target="_blank" rel="noopener">
                  <FormattedMessage
                    id="flow.form.park-domian.warning.how"
                    defaultMessage="How to do it?"
                  />
                </a>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

FlowDirectLinkForm.propTypes = propTypes;

export default injectIntl(FlowDirectLinkForm);
